import React, { useEffect, memo } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Play } from 'lucide-react';
export function FormsPage() {
  useEffect(() => {
    document.title = "Forms Training Videos | Walker's Martial Arts";
    window.scrollTo(0, 0);
  }, []);
  const trainingTips = [{
    title: 'Basics of Stances',
    description: 'These videos cover fundamental stances that form the backbone of all martial arts. Mastery of these positions is crucial for effective movement and defense.'
  }, {
    title: 'Kicking Techniques',
    description: 'Learn different kicking techniques with detailed breakdowns, demonstrating the proper mechanics for each kick. This will enhance your accuracy and power.'
  }, {
    title: 'Hand Techniques',
    description: 'Focus on various hand strikes, including punches and blocks. Proper execution will improve your effectiveness in both forms and self-defense scenarios.'
  }, {
    title: 'Flow of the Form',
    description: 'This video illustrates the complete flow of a chosen form. Pay attention to transitions, rhythm, and timing to enhance your performance.'
  }, {
    title: 'Common Mistakes',
    description: 'Discover the most frequent errors made during forms training and how to correct them. Avoiding these pitfalls will significantly elevate your skills.'
  }, {
    title: 'Visualization Techniques',
    description: 'Utilize mental imagery to enhance your forms practice. This video explores visualization methods that can aid in memorizing and executing your forms flawlessly.'
  }, {
    title: 'Form Variations',
    description: 'Explore different variations of traditional forms to understand their applications and nuances. This will provide you with a broader perspective on your training.'
  }];
  const taegeukForms = [{
    name: 'Taegeuk 1',
    id: 'bfO_UUc32dc'
  }, {
    name: 'Taegeuk 2',
    id: 'a0oo0SsS29U'
  }, {
    name: 'Taegeuk 3',
    id: 'S2ofJsatGGg'
  }, {
    name: 'Taegeuk 4',
    id: 'eSVIFCKH8EA'
  }, {
    name: 'Taegeuk 5',
    id: 'APu5dDLg6Uc'
  }, {
    name: 'Taegeuk 6',
    id: 'DSUnv5b06gY'
  }, {
    name: 'Taegeuk 7',
    id: 'jewGP4sEFv0'
  }, {
    name: 'Taegeuk 8',
    id: '8tsVR4cJvRU'
  }];
  const blackBeltForms = [{
    name: 'Koryo',
    id: 'LxJPw8z3aAI'
  }, {
    name: 'Keumgang',
    id: 'QcJrZerev4E'
  }, {
    name: 'Taebaek',
    id: 'AEnI_urP8O4'
  }, {
    name: 'Pyongwon',
    id: 'iHAJCSoZGJA'
  }, {
    name: 'Sipjin',
    id: 'YhXPzBhdwtU'
  }, {
    name: 'Jitae',
    id: 'uiidzNlj2NY'
  }, {
    name: 'Chonkwon',
    id: 'OYI_EWIqI18'
  }, {
    name: 'Hansu',
    id: 'PmpX7C0nwvU'
  }, {
    name: 'Ilyeo',
    id: 'IMqB-098i2s'
  }];
  return <div className="min-h-screen bg-black text-white" data-id="element-792">
      <Navigation data-id="element-793" />

      {/* Hero Section */}
      <div className="relative pt-32 pb-16 overflow-hidden" data-id="element-794">
        <div className="absolute inset-0 z-0" data-id="element-795">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" data-id="element-796" />
          <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/fmBXjQm2BPtC5bTSRKvwCz/walkerlogo-gigapixel-cgi-3600w.png')] bg-cover bg-center opacity-5 blur-sm" data-id="element-797" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-798">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-799">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4 tracking-tight" data-id="element-800">
              FORMS
            </h1>
            <p className="text-xl md:text-2xl text-[#00D4FF] font-medium tracking-wide uppercase" data-id="element-801">
              Training Video Library
            </p>
          </motion.div>
        </div>
      </div>

      {/* Intro Content */}
      <section className="py-12 bg-[#0a0a0a]" data-id="element-802">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-803">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-[#1a1a1a] p-8 md:p-10 rounded-2xl border border-gray-800 shadow-xl" data-id="element-804">
            <p className="text-lg text-gray-300 mb-8 leading-relaxed" data-id="element-805">
              The following videos may be used as an aid to your forms training:
            </p>

            <ol className="space-y-6 mb-10" data-id="element-806">
              {trainingTips.map((tip, index) => <li key={index} className="flex gap-4" data-id="element-807">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00D4FF]/10 text-[#00D4FF] flex items-center justify-center font-bold border border-[#00D4FF]/30" data-id="element-808">
                    {index + 1}
                  </span>
                  <div data-id="element-809">
                    <strong className="text-white block mb-1 text-lg" data-id="element-810">
                      {tip.title}
                    </strong>
                    <p className="text-gray-400 leading-relaxed" data-id="element-811">
                      {tip.description}
                    </p>
                  </div>
                </li>)}
            </ol>

            <div className="p-6 bg-black/50 rounded-xl border border-gray-800" data-id="element-812">
              <p className="text-gray-300 italic text-center" data-id="element-813">
                Use these resources to complement your practice, refine your
                technique, and elevate your performance. Contact an instructor
                for individual guidance.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Taegeuk Forms Section */}
      <section className="py-16 bg-black" data-id="element-814">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-815">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-816">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-817">
              Taegeuk Forms
            </h2>
            <div className="w-20 h-1 bg-[#00D4FF] mx-auto rounded-full" data-id="element-818" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-819">
            {taegeukForms.map((video, index) => <motion.div key={video.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-gray-800 hover:border-[#00D4FF]/50 transition-all group" data-id="element-820">
                <div className="aspect-video w-full bg-black relative" data-id="element-821">
                  <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${video.id}`} title={video.name} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-822" />
                </div>
                <div className="p-4 text-center border-t border-gray-800" data-id="element-823">
                  <h3 className="text-lg font-bold text-white group-hover:text-[#00D4FF] transition-colors flex items-center justify-center gap-2" data-id="element-824">
                    <Play className="w-4 h-4 fill-current" data-id="element-825" />
                    {video.name}
                  </h3>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Black Belt Forms Section */}
      <section className="py-16 bg-[#0a0a0a]" data-id="element-826">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-827">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-828">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-829">
              Black Belt Forms
            </h2>
            <div className="w-20 h-1 bg-[#00D4FF] mx-auto rounded-full" data-id="element-830" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-831">
            {blackBeltForms.map((video, index) => <motion.div key={video.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-gray-800 hover:border-[#00D4FF]/50 transition-all group" data-id="element-832">
                <div className="aspect-video w-full bg-black relative" data-id="element-833">
                  <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${video.id}`} title={video.name} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-834" />
                </div>
                <div className="p-4 text-center border-t border-gray-800" data-id="element-835">
                  <h3 className="text-lg font-bold text-white group-hover:text-[#00D4FF] transition-colors flex items-center justify-center gap-2" data-id="element-836">
                    <Play className="w-4 h-4 fill-current" data-id="element-837" />
                    {video.name}
                  </h3>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <Footer data-id="element-838" />
    </div>;
}